/*
 * If multiple branding in a page is not required, use the native blueprint "BrandingStyles" component.
 *
 * The implementation is rewritten from blueprint-web.
 * https://git.dev.box.net/Box/frontend-mono/blob/master/packages/blueprint-web/src/lib/branding-styles/useCustomBranding.ts
 *
 * We adopt this workaround for multiple branding in a page since the feature hasn't been supported in blueprint-web.
 * Besides, some components are from BUIE features / elements, which are not in blueprint's radar.
 * Need to consider these legacy components theme, too..
 * https://jira.inside-box.net/browse/DSYS-501
 *
 * */
import {
  BoxBlue100,
  Gray65,
  Gray100,
  BorderCtaBorderSecondary,
  GrayWhite,
} from '@box/blueprint-web-assets/tokens/tokens';
import { createTheme } from '@box/blueprint-web/lib-esm/branding-styles/utils/createTheme';
import colorLib from 'color';
import React, { useEffect } from 'react';
import type { ThemeStyleProps } from './types';

type Props = ThemeStyleProps;

const BlueprintThemeStyle = ({ color: brandColor, wrapperClass }: Props) => {
  useEffect(() => {
    if (!brandColor) {
      return undefined;
    }

    const buieTheme = createTheme(`#${brandColor}`);
    const isBrandingEnabled =
      brandColor.toLowerCase() !== BoxBlue100.toLowerCase();

    const secondaryColor = colorLib(Gray100);

    const stylesheet = document.createElement('style');
    // add data-blueprint property for debugging
    stylesheet.dataset.blueprint = '';
    stylesheet.setAttribute('data-testid', 'blueprint-theme');

    if (document.head) {
      document.head.appendChild(stylesheet);
    }

    stylesheet.textContent = `
      .${wrapperClass} {
        /* Button branding tokens */
        --text-text-brand-on-color: ${buieTheme.primary.buttonForeground};
        --surface-surface-brand: ${buieTheme.primary.buttonBackground};
        --surface-surface-brand-hover: ${
          buieTheme.primary.buttonBackgroundHover
        };
        --surface-surface-brand-pressed: ${
          buieTheme.primary.buttonBackgroundActive
        };
        --surface-surface-brand-disabled: ${buieTheme.primary.buttonBackground};
        --surface-surface-brand-busy: ${buieTheme.primary.buttonBackground};
          
        /* Nav branding tokens */
        --surface-nav-surface-brand-selected-focus: ${
          buieTheme.primary.backgroundActive
        };
        --surface-nav-surface-brand-selected: ${
          buieTheme.primary.backgroundActive
        };
        --surface-nav-surface-brand-focus: ${
          buieTheme.primary.backgroundActive
        };
        --surface-nav-surface-brand-hover: ${buieTheme.primary.backgroundHover};
        --surface-nav-surface-brand: ${buieTheme.primary.background};
        
        /* Illustrations branding tokens */
        --surface-illustration-surface-box-neutral: ${
          isBrandingEnabled ? Gray65 : BoxBlue100
        } 
      }
      
      /* BUIE element OverallUploadsProgressBar */   
      .${wrapperClass} .bcu-overall-progress-bar {
        background-color: ${secondaryColor.string()};
      }
      
      .${wrapperClass} .bcu-overall-progress-bar:hover {
        background-color: ${secondaryColor.lighten(0.2).string()};
      }
      
      .${wrapperClass} .bcu-overall-progress-bar .btn-primary {
        background-color: var(--surface-surface-brand);
        border-color: var(--surface-surface-brand);
        color: var(--text-text-brand-on-color);
      }
      
      /* Form Dropzone */
      .${wrapperClass} .dropzone {
        background-color: var(--surface-surface-secondary);
        border-color: ${secondaryColor.string()};
      }
      
      .${wrapperClass} .dropzone * {
        color: ${secondaryColor.string()};
      }
      
      .${wrapperClass} .dropzone .btn {
        color: ${secondaryColor.string()};
        border-color: ${BorderCtaBorderSecondary};
        background-color: ${GrayWhite};
      }
    `;

    return () => {
      if (document.head) {
        document.head.removeChild(stylesheet);
      }
    };
  }, [brandColor, wrapperClass]);

  return <></>;
};

export default BlueprintThemeStyle;
